import { useMemo, useState } from 'react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';

import Page from '@/components/page/Page';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/common/data-table/data-table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MapPin,
  Search,
  Award,
  Sparkles,
  Users,
  UserCheck,
  Clock,
  DollarSign,
  Timer,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

// Brand color for procurement demo
const BRAND_COLOR = '#6a89cc';

import {
  mockSuppliers,
  mockItems,
  mockPurchaseOrders,
  type Supplier,
} from '../data/mockData';

// Helper to get primary category from items supplied
function getPrimaryCategory(itemIds: string[]): string {
  const items = itemIds
    .map((id) => mockItems.find((item) => item.id === id))
    .filter(Boolean);

  if (items.length === 0) return 'General';

  const categoryMap: Record<string, string> = {
    'raw-material': 'Raw Materials',
    component: 'Components',
    packaging: 'Packaging',
  };

  // Count categories
  const categoryCounts: Record<string, number> = {};
  items.forEach((item) => {
    const cat = item!.category;
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });

  // Return most common category
  const primaryCat = Object.entries(categoryCounts).sort(
    (a, b) => b[1] - a[1],
  )[0][0];
  return categoryMap[primaryCat] || 'General';
}

// Helper to calculate total spend for a supplier
function getSupplierTotalSpend(supplierId: string): number {
  return mockPurchaseOrders
    .filter((po) => po.supplierId === supplierId)
    .reduce((sum, po) => sum + po.totalAmount, 0);
}

// Helper to format currency
function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
}

// Helper to get performance rating
function getPerformanceRating(qualityScore: number): {
  label: string;
  className: string;
} {
  if (qualityScore >= 95) {
    return { label: 'Excellent', className: 'bg-green-100 text-green-700' };
  }
  if (qualityScore >= 85) {
    return { label: 'Good', className: 'bg-blue-100 text-blue-700' };
  }
  if (qualityScore >= 70) {
    return { label: 'Fair', className: 'bg-yellow-100 text-yellow-700' };
  }
  return { label: 'Poor', className: 'bg-red-100 text-red-700' };
}

// Helper to get on-time color
function getOnTimeColor(score: number): string {
  if (score >= 95) return 'text-green-600';
  if (score >= 90) return 'text-blue-600';
  if (score >= 85) return 'text-yellow-600';
  return 'text-red-600';
}

export function SuppliersPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const columns: ColumnDef<Supplier>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: () => (
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            Supplier
          </span>
        ),
        cell: ({ row }) => {
          const supplier = row.original;
          const isTopPerformer = supplier.qualityScore >= 95;

          return (
            <div className="flex items-center gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {supplier.name}
                  </span>
                  {isTopPerformer && (
                    <Award className="w-4 h-4 text-yellow-500" />
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                  <MapPin className="w-3 h-3" />
                  {supplier.location}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: 'category',
        header: () => (
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            Category
          </span>
        ),
        cell: ({ row }) => {
          const category = getPrimaryCategory(row.original.itemsSupplied);
          return <span className="text-sm text-gray-900">{category}</span>;
        },
      },
      {
        accessorKey: 'qualityScore',
        header: () => (
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            Rating
          </span>
        ),
        cell: ({ row }) => {
          const supplier = row.original;
          const rating = (supplier.qualityScore / 20).toFixed(1);
          const orderCount = mockPurchaseOrders.filter(
            (po) => po.supplierId === supplier.id,
          ).length;

          return (
            <div>
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-gray-900">
                  {rating}
                </span>
                <span className="text-xs text-gray-500">/5</span>
              </div>
              <div className="text-xs text-gray-500">
                {supplier.totalOrders} orders
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'reliabilityScore',
        header: () => (
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            On-Time %
          </span>
        ),
        cell: ({ row }) => {
          const score = row.original.reliabilityScore;
          return (
            <div className={`text-sm font-semibold ${getOnTimeColor(score)}`}>
              {score}%
            </div>
          );
        },
      },
      {
        accessorKey: 'leadTimeWeeks',
        header: () => (
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            Lead Time
          </span>
        ),
        cell: ({ row }) => {
          const days = row.original.leadTimeWeeks * 7;
          return <span className="text-sm text-gray-900">{days} days</span>;
        },
      },
      {
        id: 'totalSpend',
        header: () => (
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            Total Spend
          </span>
        ),
        cell: ({ row }) => {
          const totalSpend = getSupplierTotalSpend(row.original.id);
          return (
            <span className="text-sm font-medium text-gray-900">
              {formatCurrency(totalSpend)}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: () => (
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            Status
          </span>
        ),
        cell: ({ row }) => {
          const supplier = row.original;
          const statusConfig = {
            active: {
              label: 'Active',
              className: 'bg-green-100 text-green-700',
            },
            pending: {
              label: 'Review',
              className: 'bg-yellow-100 text-yellow-700',
            },
            inactive: {
              label: 'Inactive',
              className: 'bg-gray-100 text-gray-700',
            },
          };
          const status = statusConfig[supplier.status];
          const performance = getPerformanceRating(supplier.qualityScore);

          return (
            <div className="flex flex-col gap-1">
              <span
                className={`px-2 py-1 rounded text-xs font-medium inline-block w-fit ${status.className}`}
              >
                {status.label}
              </span>
              <span
                className={`px-2 py-1 rounded text-xs font-medium inline-block w-fit ${performance.className}`}
              >
                {performance.label}
              </span>
            </div>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: mockSuppliers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      globalFilter,
    },
  });

  // Calculate stats
  const stats = useMemo(() => {
    const totalSuppliers = mockSuppliers.length;
    const activeSuppliers = mockSuppliers.filter(
      (s) => s.status === 'active',
    ).length;
    const avgOnTime = Math.round(
      mockSuppliers.reduce((sum, s) => sum + s.reliabilityScore, 0) /
        totalSuppliers,
    );
    const totalSpendYTD = mockPurchaseOrders.reduce(
      (sum, po) => sum + po.totalAmount,
      0,
    );
    const avgLeadTimeDays = Math.round(
      (mockSuppliers.reduce((sum, s) => sum + s.leadTimeWeeks, 0) /
        totalSuppliers) *
        7,
    );

    return {
      totalSuppliers,
      activeSuppliers,
      avgOnTime,
      totalSpendYTD,
      avgLeadTimeDays,
    };
  }, []);

  return (
    <Page
      title="Suppliers"
      subtitle={`Managing ${stats.totalSuppliers} supplier relationships`}
      actions={
        <Button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Find New Suppliers
        </Button>
      }
    >
      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5 mb-6">
        {[
          {
            title: 'Total Suppliers',
            value: stats.totalSuppliers.toString(),
            trend: { value: '+3', direction: 'up' as const },
            icon: Users,
          },
          {
            title: 'Active Suppliers',
            value: stats.activeSuppliers.toString(),
            trend: {
              value: `${Math.round((stats.activeSuppliers / stats.totalSuppliers) * 100)}%`,
              direction: 'up' as const,
            },
            icon: UserCheck,
          },
          {
            title: 'Avg On-Time Rate',
            value: `${stats.avgOnTime}%`,
            trend: { value: '+2.1%', direction: 'up' as const },
            icon: Clock,
          },
          {
            title: 'Total Spend YTD',
            value: formatCurrency(stats.totalSpendYTD),
            trend: { value: '+15.3%', direction: 'up' as const },
            icon: DollarSign,
          },
          {
            title: 'Avg Lead Time',
            value: `${stats.avgLeadTimeDays} days`,
            trend: { value: '-2 days', direction: 'down' as const },
            icon: Timer,
          },
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card
              key={index}
              className="relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 group"
              style={{ borderColor: 'transparent' }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background: `linear-gradient(to right, ${BRAND_COLOR}40, transparent)`,
                }}
              />
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="p-2.5 rounded-xl"
                    style={{ backgroundColor: `${BRAND_COLOR}15` }}
                  >
                    <Icon className="h-4 w-4" style={{ color: BRAND_COLOR }} />
                  </div>
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge
                        variant="secondary"
                        className={`text-xs font-medium cursor-help ${
                          stat.trend.direction === 'up'
                            ? 'bg-emerald-500/10 text-emerald-500'
                            : 'bg-rose-500/10 text-rose-500'
                        }`}
                      >
                        {stat.trend.direction === 'up' ? (
                          <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                          <TrendingDown className="h-3 w-3 mr-1" />
                        )}
                        {stat.trend.value}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>vs last month</TooltipContent>
                  </Tooltip>
                </div>
                <div className="space-y-0.5">
                  <p className="text-2xl font-bold tracking-tight">
                    {stat.value}
                  </p>
                  <p className="text-xs text-muted-foreground">{stat.title}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search Bar */}
      <div className="flex items-center justify-end mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search suppliers..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="pl-9 w-[220px] h-9"
          />
        </div>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <DataTable table={table} />
      </Card>
    </Page>
  );
}
